import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { PillInput, PillButton } from '../components/PillKit';
import { authExtrasAPI, formatApiError } from '../services/api';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authExtrasAPI.forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="forgot-password-page">
      <header className="px-5 pt-6 pb-2 flex items-center justify-between">
        <Link to="/login" className="text-white p-1" aria-label="Back to login" data-testid="forgot-back-to-login">
          <CaretLeft className="w-6 h-6" />
        </Link>
        <Link to="/" data-testid="forgot-logo-link">
          <img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" />
        </Link>
        <span className="w-6" />
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-8 pb-12">
        {sent ? (
          <div data-testid="forgot-sent-panel" className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#22C55E]/15 border-[3px] border-[#22C55E] flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Check your inbox
            </h1>
            <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-8">
              If <span className="text-white font-semibold">{email}</span> is a registered Divispend account, we sent a reset link. It expires in 1 hour.
            </p>
            <div className="space-y-3">
              <Link to="/login"><PillButton variant="solid" data-testid="forgot-back-btn" type="button">Back to sign in</PillButton></Link>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(''); }}
                className="block w-full text-sm text-white/60 hover:text-white underline underline-offset-2 py-2"
                data-testid="forgot-resend-btn"
              >
                Didn&apos;t receive it? Try a different email
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-white/5 border-[3px] border-white flex items-center justify-center mb-5">
                <EnvelopeSimple className="w-8 h-8 text-[#22C55E]" weight="duotone" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold leading-none tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Forgot password?
              </h1>
              <p className="text-[#22C55E] text-base sm:text-lg font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                We&apos;ll send you a reset link
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <PillInput
                id="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email-input"
              />
              <PillButton
                type="submit"
                variant="solid"
                disabled={loading || !email}
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </PillButton>
            </form>

            <p className="text-sm text-white/50 text-center mt-8">
              Remembered? <Link to="/login" className="text-[#22C55E] font-semibold hover:underline">Sign in</Link>
            </p>
          </>
        )}
      </main>
    </div>
  );
}
